/**
 * The `/`-triggered command menu inside the notes editor — §2.8 #65.
 *
 * Lifted verbatim out of `NotesViewer.tsx`, where it sat above the two editor
 * components that both call it (`ApiNotesEditor` and `MockNotesEditor`). The
 * move is behaviour-preserving: same state, same effects, same relative order.
 *
 * ---
 * **Why this owns document-level listeners rather than Quill keybindings.**
 *
 * The obvious implementation is `modules.keyboard.bindings`, and it is the
 * wrong one here: the menu has to keep responding to ArrowUp/ArrowDown/Enter
 * *while the caret is still in the editor*, and a Quill binding for Enter
 * would have to decide between "move the selection in the menu" and "insert a
 * newline" from inside Quill's own handler chain. Listening on the document
 * and calling `preventDefault` when — and only when — the menu is open keeps
 * that decision in one place.
 *
 * The cost is two refs that look redundant and are not:
 *
 * - `selectedCommandIndexRef` shadows `selectedCommandIndex` because the
 *   keydown listener closes over the value from the render that installed it.
 *   The setter updates the ref alongside the state so Enter reads the index
 *   the user can actually see.
 * - `isClickingMenuRef` suppresses the keyboard handler for 200ms after a
 *   mouse click on a menu item, so the click's synthetic focus churn cannot
 *   race the Enter path into applying a command twice.
 *
 * `isApplyingCommandRef` is the third, and the most important: `applySlashCommand`
 * writes to the document with Quill's `'silent'` source, but the `deleteText`
 * still re-enters `handleSlashDetection` through React's own change plumbing on
 * some paths. Without the guard, applying a command re-opens the menu it just
 * closed.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Code,
  Type,
} from 'lucide-react';
import type ReactQuill from 'react-quill';

/** Slash command definitions (shared between mock and API modes). */
export const slashCommands = [
  { id: 'h1', label: 'Heading 1', icon: Heading1, format: 'header', value: 1 },
  { id: 'h2', label: 'Heading 2', icon: Heading2, format: 'header', value: 2 },
  { id: 'h3', label: 'Heading 3', icon: Heading3, format: 'header', value: 3 },
  {
    id: 'text',
    label: 'Normal Text',
    icon: Type,
    format: 'header',
    value: false,
  },
  {
    id: 'bullet',
    label: 'Bullet List',
    icon: List,
    format: 'list',
    value: 'bullet',
  },
  {
    id: 'numbered',
    label: 'Numbered List',
    icon: ListOrdered,
    format: 'list',
    value: 'ordered',
  },
  {
    id: 'quote',
    label: 'Quote',
    icon: Quote,
    format: 'blockquote',
    value: true,
  },
  {
    id: 'code',
    label: 'Code Block',
    icon: Code,
    format: 'code-block',
    value: true,
  },
];

export type SlashCommand = (typeof slashCommands)[0];

/** Everything `SlashMenu` needs to render, plus the two editor callbacks. */
export interface SlashMenuState {
  showSlashMenu: boolean;
  slashMenuPosition: { top: number; left: number };
  selectedCommandIndex: number;
  filteredCommands: SlashCommand[];
  slashMenuRef: React.RefObject<HTMLDivElement>;
  isClickingMenuRef: React.MutableRefObject<boolean>;
  handleSlashDetection: (
    content: string,
    delta: unknown,
    source: string,
    editor: SlashDetectionEditor
  ) => void;
  applySlashCommand: (command: SlashCommand) => void;
}

/**
 * The slice of Quill's unprivileged editor `handleSlashDetection` reads.
 *
 * Narrowed rather than typed as `any` so a story (or a future caller) can hand
 * in a stand-in and have the compiler check it. `getBounds` is what forces this
 * to be an interface at all — it is the only part that needs real layout.
 */
export interface SlashDetectionEditor {
  getSelection: () => { index: number; length: number } | null;
  getText: () => string;
  getBounds: (index: number) => { bottom: number; left: number };
}

/** The `/` must start a line or follow a space; anything else is just a slash. */
const MAX_SLASH_QUERY_LENGTH = 20;

export function useSlashMenu(
  quillRef: React.RefObject<ReactQuill>
): SlashMenuState {
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashMenuPosition, setSlashMenuPosition] = useState({
    top: 0,
    left: 0,
  });
  const [slashMenuSearch, setSlashMenuSearch] = useState('');
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);
  const [slashIndex, setSlashIndex] = useState(-1);
  const isApplyingCommandRef = useRef(false);
  const applyCommandRef = useRef<((command: SlashCommand) => void) | null>(
    null
  );
  const isClickingMenuRef = useRef(false);
  const selectedCommandIndexRef = useRef(0);
  const slashMenuRef = useRef<HTMLDivElement>(null);

  const filteredCommands = slashCommands.filter(cmd =>
    cmd.label.toLowerCase().includes(slashMenuSearch.toLowerCase())
  );

  const handleSlashDetection = useCallback(
    (
      content: string,
      delta: unknown,
      source: string,
      editor: SlashDetectionEditor
    ) => {
      if (isApplyingCommandRef.current) return;
      if (source !== 'user') return;

      const selection = editor.getSelection();
      if (!selection) {
        if (showSlashMenu) {
          setShowSlashMenu(false);
          setSlashMenuSearch('');
        }
        return;
      }

      const text = editor.getText();
      const textBeforeCursor = text.substring(0, selection.index);
      const lastSlashIdx = textBeforeCursor.lastIndexOf('/');

      if (
        lastSlashIdx !== -1 &&
        selection.index - lastSlashIdx <= MAX_SLASH_QUERY_LENGTH
      ) {
        const searchText = textBeforeCursor.substring(lastSlashIdx + 1).trim();
        const charBeforeSlash =
          lastSlashIdx > 0 ? textBeforeCursor[lastSlashIdx - 1] : '\n';

        if (
          charBeforeSlash === '\n' ||
          charBeforeSlash === ' ' ||
          lastSlashIdx === 0
        ) {
          setSlashIndex(lastSlashIdx);
          setSlashMenuSearch(searchText);
          const bounds = editor.getBounds(selection.index);
          setSlashMenuPosition({ top: bounds.bottom + 5, left: bounds.left });
          if (!showSlashMenu) {
            setShowSlashMenu(true);
            setSelectedCommandIndex(0);
            selectedCommandIndexRef.current = 0;
          }
          return;
        }
      }

      if (showSlashMenu) {
        setShowSlashMenu(false);
        setSlashMenuSearch('');
      }
    },
    [showSlashMenu]
  );

  const applySlashCommand = useCallback(
    (command: SlashCommand) => {
      if (!quillRef.current) return;
      const quill = quillRef.current.getEditor();
      if (slashIndex === -1) return;

      isApplyingCommandRef.current = true;
      const selection = quill.getSelection();
      if (!selection) return;

      const deleteLength = selection.index - slashIndex;
      quill.deleteText(slashIndex, deleteLength, 'silent');
      quill.formatLine(slashIndex, 1, command.format, command.value, 'silent');
      quill.setSelection(slashIndex, 0, 'silent');

      setShowSlashMenu(false);
      setSlashMenuSearch('');
      setSlashIndex(-1);

      setTimeout(() => {
        isApplyingCommandRef.current = false;
        quill.focus();
      }, 100);
    },
    [quillRef, slashIndex]
  );

  applyCommandRef.current = applySlashCommand;

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showSlashMenu) return;
      if (isClickingMenuRef.current) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedCommandIndex(prev => {
          const next = prev < filteredCommands.length - 1 ? prev + 1 : prev;
          selectedCommandIndexRef.current = next;
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedCommandIndex(prev => {
          const next = prev > 0 ? prev - 1 : prev;
          selectedCommandIndexRef.current = next;
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (
          filteredCommands[selectedCommandIndexRef.current] &&
          applyCommandRef.current
        ) {
          applyCommandRef.current(
            filteredCommands[selectedCommandIndexRef.current]
          );
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowSlashMenu(false);
        setSlashMenuSearch('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showSlashMenu, filteredCommands]);

  // Click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!showSlashMenu) return;
      if (
        slashMenuRef.current &&
        !slashMenuRef.current.contains(e.target as Node)
      ) {
        setShowSlashMenu(false);
        setSlashMenuSearch('');
        setSlashIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSlashMenu]);

  return {
    showSlashMenu,
    slashMenuPosition,
    selectedCommandIndex,
    filteredCommands,
    slashMenuRef,
    isClickingMenuRef,
    handleSlashDetection,
    applySlashCommand,
  };
}
